import React, { useState } from "react";
import S from "./strings";
import Spectrum from "./Spectrum";
import Development from "./Development";
import DeleteDialog from "./DeleteDialog";
import Toggle from "../../common/Toggle";
import { isRtlLanguage, monthYear } from "./locale";
import useUiLanguage from "./useUiLanguage";
import { coachingPersonalityService } from "../../../services/coachingPersonalityApi";

// COACHING PERSONALITY — the inner evaluation. Only its owner ever sees this.
//
// Konzept 4a lists what belongs here and this renders that list: the five axes
// as spectra with a marker, per axis the reading plus the two or three
// sentences on what it means in a coach's week, the as-of date, the note on the
// next possible update, and under it the release switch.
//
// TWO THINGS THE PROTOTYPE DRAWS AND THIS DOES NOT, both on purpose:
//
//   * the two-step release picker ("Clubs I apply to" / "All clubs on
//     TransferPitch"). Konzept 4a rules it out in as many words: "No further
//     levels, no club lists, no 'all'." There is ONE switch, default off.
//
//   * the three level labels printed as zones under each spectrum. The server
//     sends the label of the level a person actually reached and no other
//     (coaching_personality.py:181-193) — the other two would have to be a
//     second copy of the catalogue living in the client, which is exactly the
//     second truth the house rules forbid. Reported as a one line backend
//     addition rather than invented here.
//
// The client computes nothing: sum, level, short label and marker position all
// arrive worked out.

const AxisBlock = ({ axis, isLast, rtl }) => (
  <div
    className={`p-[clamp(18px,3vw,26px)] ${
      isLast ? "" : "border-b border-[#E5E7EB]"
    }`}
  >
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <div className="flex-1 min-w-[180px]">
        <div className="text-[15px] font-bold text-[#111827]">{axis.name}</div>
      </div>
      <div className="text-[13px] font-semibold text-[#111827]">{axis.label}</div>
    </div>

    {/* Pole labels may wrap to two lines (layout rule 2); the marker below is
        positioned against the track, so nothing about it moves when they do. */}
    <div className="flex justify-between gap-3 mt-[14px] text-[12px] text-[#6B7280]">
      <span className="max-w-[48%] min-w-0">{axis.pole_left}</span>
      <span className="max-w-[48%] min-w-0 text-end">{axis.pole_right}</span>
    </div>

    <Spectrum position={axis.marker_position} size="inner" rtl={rtl} />

    {/* Flowing text, no height cap, never shortened (layout rules 3 and 4). */}
    <div className="mt-[14px] bg-[#F5F7FB] rounded-[8px] px-[14px] py-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[#6B7280]">
        {S.levelEyebrow}
      </div>
      <p className="mt-[6px] text-[14px] leading-[1.55] text-[#374151] [text-wrap:pretty]">
        {axis.text}
      </p>
    </div>
  </div>
);

const Evaluation = ({
  result,
  lock,
  evalIntro,
  history,
  catalogVersion,
  onReleaseChanged,
  onDeleted,
  onRetake,
}) => {
  const language = useUiLanguage();
  const rtl = isRtlLanguage(language);
  const [released, setReleased] = useState(!!result?.is_released);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!result) return null;

  const axes = result.axes || [];
  const asOfDate = monthYear(result.completed_at, language);
  const canRetake = lock ? !!lock.can_retake : true;
  const retakeAt = lock?.retake_available_at
    ? monthYear(lock.retake_available_at, language)
    : "";
  // The same sentence serves the caption under "Retake questionnaire" and the
  // warning in the delete dialog, because it is the same fact.
  const lockLine = canRetake ? S.due : retakeAt ? S.lockUntil(retakeAt) : "";

  const toggleRelease = async (value) => {
    setReleased(value);
    const response = await coachingPersonalityService.setReleased(value);
    if (!response.success) {
      setReleased(!value);
      return;
    }
    if (onReleaseChanged) onReleaseChanged(response.data?.is_released);
  };

  const confirmDelete = async () => {
    setBusy(true);
    const response = await coachingPersonalityService.deleteResult();
    setBusy(false);
    setConfirming(false);
    if (response.success && onDeleted) onDeleted(response.data);
  };

  return (
    <div dir={rtl ? "rtl" : undefined}>
      <div className="mt-[14px]">
        <p className="m-0 text-[13px] leading-[1.5] text-[#374151] [text-wrap:pretty]">
          {S.resultIntro}
        </p>
        {evalIntro && (
          <p className="mt-2 text-[13px] leading-[1.5] text-[#6B7280] [text-wrap:pretty]">
            {evalIntro}
          </p>
        )}
        {/* Date and lock note are two registered sentences: two elements, with
            the separator as markup. */}
        <div className="mt-[6px] flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] text-[#6B7280]">
          <span>{S.asOf(asOfDate)}</span>
          {lockLine && (
            <>
              <span aria-hidden="true">&middot;</span>
              <span>{lockLine}</span>
            </>
          )}
        </div>
      </div>

      <div className="border border-[#E5E7EB] rounded-[8px] mt-4">
        {axes.map((axis, position) => (
          <AxisBlock
            key={axis.axis}
            axis={axis}
            isLast={position === axes.length - 1}
            rtl={rtl}
          />
        ))}
      </div>

      <Development results={history} />

      {/* ONE switch, default off. */}
      <div className="border border-[#E5E7EB] rounded-[8px] p-5 mt-4">
        <div className="text-[15px] font-bold text-[#111827]">
          {S.releaseTitle}
        </div>
        <p className="mt-1 text-[13px] leading-[1.5] text-[#6B7280]">
          {S.releaseExplainer}
        </p>
        {/* The labelled switch row is the shape LawyerProfileForm.js:325-341
            already established: title plus grey subline on one side, switch on
            the other, both growing with their content. */}
        <div className="flex items-center justify-between gap-4 mt-[14px]">
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-[#344054]">
              {S.toggleLabel}
            </p>
            <p className="text-[12px] leading-[1.5] text-[#98A2B3] mt-[2px]">
              {S.toggleExplainer}
            </p>
          </div>
          <Toggle
            checked={released}
            onChange={toggleRelease}
            label={S.toggleLabel}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-4 mt-5">
        <div>
          <button
            type="button"
            disabled={!canRetake}
            onClick={() => canRetake && onRetake && onRetake()}
            className={`min-h-[44px] px-5 rounded-[8px] border-none bg-[#E9F2FF] text-[#2183FE] text-[14px] font-semibold ${
              canRetake ? "" : "opacity-50 cursor-default"
            }`}
          >
            {S.retake}
          </button>
          {lockLine && (
            <div className="mt-[6px] text-[12px] text-[#6B7280]">{lockLine}</div>
          )}
        </div>
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="min-h-[44px] px-[10px] bg-transparent border-none text-[#6B7280] text-[13px] hover:text-[#111827]"
        >
          {S.delete}
        </button>
      </div>

      {/* Five registered sentences, five elements, dots as markup. */}
      <div className="mt-5 pt-[14px] border-t border-[#E5E7EB] flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] leading-[1.6] text-[#9CA3AF]">
        <span>{S.tagSelfAssessment}</span>
        <span aria-hidden="true">&middot;</span>
        <span>{S.tagBigFive}</span>
        <span aria-hidden="true">&middot;</span>
        <span>{S.catalogVersion(catalogVersion || result.catalog_version)}</span>
        <span aria-hidden="true">&middot;</span>
        <span>{S.footerVersion}</span>
        <span aria-hidden="true">&middot;</span>
        <span>{S.footerLanguage}</span>
      </div>

      {confirming && (
        <DeleteDialog
          lockLine={lockLine}
          busy={busy}
          onCancel={() => setConfirming(false)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
};

export default Evaluation;
